import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import milestoneTrackingService from '../../services/milestoneTrackingService';
import companyAccountsService from '../../services/companyAccountsService';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import ClientProgressMatrix from './components/ClientProgressMatrix';
import BottleneckAnalysisPanel from './components/BottleneckAnalysisPanel';
import ClientDrillDownModal from './components/ClientDrillDownModal';

const ClientOnboardingProgressTrackingDashboard = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [filters, setFilters] = useState({
    setupStage: 'all',
    completionPercentage: 'all',
    timeInStage: 'all',
    riskLevel: 'all',
    searchTerm: ''
  });

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all companies
      const companiesData = await companyAccountsService?.getAllCompanyAccounts();
      
      // Get milestones for each company
      const clientsWithMilestones = await Promise.all(
        companiesData?.map(async (company) => {
          const { data: milestones } = await milestoneTrackingService?.getCompanyMilestones(company?.id);
          const overallProgress = milestoneTrackingService?.calculateOnboardingProgress(milestones || []);
          const nextMilestone = milestoneTrackingService?.getNextMilestone(milestones || []);
          
          // Calculate time in current stage
          const timeInStage = calculateTimeInStage(nextMilestone);
          const riskLevel = calculateRiskLevel(overallProgress, timeInStage);
          
          return {
            ...company,
            milestones: milestones || [],
            overallProgress,
            nextMilestone,
            timeInStage,
            riskLevel
          };
        })
      );

      setClients(clientsWithMilestones);
      
      // Calculate summary statistics
      const summaryData = calculateSummary(clientsWithMilestones);
      setSummary(summaryData);
      
      // Identify bottlenecks
      const bottleneckData = identifyBottlenecks(clientsWithMilestones);
      setBottlenecks(bottleneckData);
    } catch (err) {
      setError(err?.message || 'Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [clients, filters]);

  const calculateTimeInStage = (milestone) => {
    if (!milestone || !milestone?.started_at) return 0;
    const startDate = new Date(milestone?.started_at);
    const now = new Date();
    return Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
  };

  const calculateRiskLevel = (progress, timeInStage) => {
    if (progress >= 75) return 'low';
    if (progress >= 50 && timeInStage < 7) return 'medium';
    if (progress < 50 || timeInStage >= 14) return 'high';
    return 'medium';
  };

  const calculateSummary = (clientsData) => {
    const totalActive = clientsData?.filter(c => c?.overallProgress < 100)?.length;
    const totalCompleted = clientsData?.filter(c => c?.overallProgress === 100)?.length;
    const completionRate = totalActive > 0 ? ((totalCompleted / (totalActive + totalCompleted)) * 100)?.toFixed(1) : 0;
    
    const avgDuration = clientsData?.reduce((sum, c) => {
      const completedMilestones = c?.milestones?.filter(m => m?.milestone_status === 'completed');
      if (completedMilestones?.length > 0) {
        const totalTime = completedMilestones?.reduce((s, m) => {
          if (m?.started_at && m?.completed_at) {
            return s + (new Date(m?.completed_at) - new Date(m?.started_at));
          }
          return s;
        }, 0);
        return sum + (totalTime / completedMilestones?.length);
      }
      return sum;
    }, 0) / clientsData?.length;
    
    const avgDurationDays = Math.round(avgDuration / (1000 * 60 * 60 * 24));
    
    const criticalBottlenecks = clientsData?.filter(c => c?.riskLevel === 'high')?.length;

    return {
      totalActive,
      completionRate,
      avgDurationDays,
      criticalBottlenecks
    };
  };

  const identifyBottlenecks = (clientsData) => {
    const bottleneckMap = {};
    
    clientsData?.forEach(client => {
      const nextMilestone = client?.nextMilestone;
      if (nextMilestone && client?.timeInStage >= 7) {
        const key = nextMilestone?.milestone_type;
        if (!bottleneckMap?.[key]) {
          bottleneckMap[key] = {
            milestoneType: key,
            affectedClients: 0,
            avgTimeStuck: 0,
            severity: 'medium'
          };
        }
        bottleneckMap[key].affectedClients += 1;
        bottleneckMap[key].avgTimeStuck += client?.timeInStage;
      }
    });
    
    return Object.values(bottleneckMap)?.map(b => ({
      ...b,
      avgTimeStuck: Math.round(b?.avgTimeStuck / b?.affectedClients),
      severity: b?.affectedClients >= 5 ? 'critical' : b?.affectedClients >= 3 ? 'high' : 'medium'
    }))?.sort((a, b) => b?.affectedClients - a?.affectedClients);
  };

  const applyFilters = () => {
    let filtered = [...clients];

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(client =>
        client?.companyName?.toLowerCase()?.includes(term) ||
        client?.contactEmail?.toLowerCase()?.includes(term)
      );
    }

    // Setup stage filter
    if (filters?.setupStage !== 'all') {
      filtered = filtered?.filter(client => 
        client?.nextMilestone?.milestone_type === filters?.setupStage
      );
    }

    // Completion percentage filter
    if (filters?.completionPercentage !== 'all') {
      filtered = filtered?.filter(client => {
        const progress = client?.overallProgress || 0;
        if (filters?.completionPercentage === 'high') return progress >= 75;
        if (filters?.completionPercentage === 'medium') return progress >= 50 && progress < 75;
        if (filters?.completionPercentage === 'low') return progress < 50;
        return true;
      });
    }

    // Time in stage filter
    if (filters?.timeInStage !== 'all') {
      filtered = filtered?.filter(client => {
        const time = client?.timeInStage || 0;
        if (filters?.timeInStage === 'recent') return time < 3;
        if (filters?.timeInStage === 'normal') return time >= 3 && time < 7;
        if (filters?.timeInStage === 'delayed') return time >= 7;
        return true;
      });
    }

    // Risk level filter
    if (filters?.riskLevel !== 'all') {
      filtered = filtered?.filter(client => client?.riskLevel === filters?.riskLevel);
    }

    setFilteredClients(filtered);
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setShowDrillDown(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleBulkIntervention = async () => {
    // Implement bulk intervention logic
    alert('Bulk intervention feature coming soon');
  };

  if (loading && clients?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={() => {}} />
        <ComplianceAlertBanner />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading onboarding data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={() => {}} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator lastRefresh={new Date()} autoRefresh={true} />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Client Onboarding Progress Tracking
              </h1>
              <p className="text-muted-foreground">
                Real-time monitoring of client setup milestones with bottleneck identification and intervention capabilities
              </p>
            </div>
            <Button
              onClick={loadData}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/20 text-error rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <ExecutiveSummaryCard
            title="Active Setups"
            value={summary?.totalActive || 0}
            subtitle="Clients in onboarding"
            icon="Users"
            status="info"
          />
          <ExecutiveSummaryCard
            title="Completion Rate"
            value={`${summary?.completionRate || 0}%`}
            subtitle="Overall success rate"
            icon="CheckCircle"
            status={summary?.completionRate >= 80 ? 'compliant' : 'warning'}
          />
          <ExecutiveSummaryCard
            title="Avg Setup Duration"
            value={`${summary?.avgDurationDays || 0} days`}
            subtitle="Per milestone completion"
            icon="Clock"
            status="info"
          />
          <ExecutiveSummaryCard
            title="Critical Bottlenecks"
            value={summary?.criticalBottlenecks || 0}
            subtitle="Requiring immediate attention"
            icon="AlertTriangle"
            status={summary?.criticalBottlenecks > 0 ? 'critical' : 'compliant'}
          />
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">Advanced Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Input
              placeholder="Search clients..."
              value={filters?.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e?.target?.value)}
              className="w-full"
            />
            <Select
              value={filters?.setupStage}
              onChange={(e) => handleFilterChange('setupStage', e?.target?.value)}
              className="w-full"
            >
              <option value="all">All Stages</option>
              <option value="organization_profile">Organization Profile</option>
              <option value="employee_import">Employee Import</option>
              <option value="compliance_setup">Compliance Setup</option>
              <option value="team_access">Team Access</option>
            </Select>
            <Select
              value={filters?.completionPercentage}
              onChange={(e) => handleFilterChange('completionPercentage', e?.target?.value)}
              className="w-full"
            >
              <option value="all">All Progress</option>
              <option value="high">75-100%</option>
              <option value="medium">50-74%</option>
              <option value="low">&lt;50%</option>
            </Select>
            <Select
              value={filters?.timeInStage}
              onChange={(e) => handleFilterChange('timeInStage', e?.target?.value)}
              className="w-full"
            >
              <option value="all">All Durations</option>
              <option value="recent">&lt;3 days</option>
              <option value="normal">3-7 days</option>
              <option value="delayed">&gt;7 days</option>
            </Select>
            <Select
              value={filters?.riskLevel}
              onChange={(e) => handleFilterChange('riskLevel', e?.target?.value)}
              className="w-full"
            >
              <option value="all">All Risk Levels</option>
              <option value="low">Low Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="high">High Risk</option>
            </Select>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Client Progress Matrix */}
          <div className="lg:col-span-2">
            <ClientProgressMatrix
              clients={filteredClients}
              onClientClick={handleClientClick}
              loading={loading}
            />
          </div>

          {/* Bottleneck Analysis Panel */}
          <div className="lg:col-span-1">
            <BottleneckAnalysisPanel
              bottlenecks={bottlenecks}
              onBulkIntervention={handleBulkIntervention}
            />
          </div>
        </div>
      </main>

      {/* Client Drill-Down Modal */}
      {showDrillDown && selectedClient && (
        <ClientDrillDownModal
          client={selectedClient}
          onClose={() => setShowDrillDown(false)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
};

export default ClientOnboardingProgressTrackingDashboard;